import { gql } from "@apollo/client";
import { useMemo } from "react";
import useSWR from "swr";
import { zeroAddress } from "viem";

import { getSubsquidGraphClient } from "lib/indexers";
import { metrics } from "lib/metrics";
import { MarketInfo as SquidMarketInfo } from "sdk/codegen/subsquid";
import { convertTokenAddress, getToken, isValidTokenSafe } from "sdk/configs/tokens";
import { queryPaginated } from "sdk/utils/indexers";

import { getMarketFullName, RawMarketsInfoData } from "..";

const MARKETS_INFO_QUERY = gql`
  query MarketsInfo($limit: Int, $offset: Int) {
    marketInfos(limit: $limit, offset: $offset) {
      marketTokenAddress
      indexTokenAddress
      longTokenAddress
      shortTokenAddress

      isDisabled

      longPoolAmount
      shortPoolAmount

      maxLongPoolAmount
      maxShortPoolAmount
      maxLongPoolUsdForDeposit
      maxShortPoolUsdForDeposit

      poolValueMax
      poolValueMin

      reserveFactorLong
      reserveFactorShort

      openInterestReserveFactorLong
      openInterestReserveFactorShort

      maxOpenInterestLong
      maxOpenInterestShort

      fundingFactor
      fundingExponentFactor
      fundingIncreaseFactorPerSecond
      fundingDecreaseFactorPerSecond
      thresholdForStableFunding
      thresholdForDecreaseFunding
      minFundingFactorPerSecond
      maxFundingFactorPerSecond

      totalBorrowingFees

      positionImpactPoolAmount
      minPositionImpactPoolAmount
      positionImpactPoolDistributionRate

      minCollateralFactor
      minCollateralFactorForLiquidation
      minCollateralFactorForOpenInterestLong
      minCollateralFactorForOpenInterestShort

      swapImpactPoolAmountLong
      swapImpactPoolAmountShort

      maxPnlFactorForTradersLong
      maxPnlFactorForTradersShort

      longOpenInterestUsd
      shortOpenInterestUsd
      longOpenInterestInTokens
      shortOpenInterestInTokens

      positionFeeFactorForPositiveImpact
      positionFeeFactorForNegativeImpact
      positionImpactFactorPositive
      positionImpactFactorNegative
      maxPositionImpactFactorPositive
      maxPositionImpactFactorNegative
      maxLendableImpactFactor
      maxLendableImpactFactorForWithdrawals
      maxLendableImpactUsd
      lentPositionImpactPoolAmount
      atomicSwapFeeFactor
      maxPositionImpactFactorForLiquidations
      positionImpactExponentFactorPositive
      positionImpactExponentFactorNegative

      swapFeeFactorForPositiveImpact
      swapFeeFactorForNegativeImpact
      swapImpactFactorPositive
      swapImpactFactorNegative
      swapImpactExponentFactor

      borrowingFactorPerSecondForLongs
      borrowingFactorPerSecondForShorts

      fundingFactorPerSecond
      longsPayShorts

      virtualPoolAmountForLongToken
      virtualPoolAmountForShortToken
      virtualInventoryForPositions

      useOpenInterestInTokensForBalance

      virtualMarketId
      virtualLongTokenId
      virtualShortTokenId
    }
  }
`;

const MARKETS_INFO_QUERY_LIMIT = 150;

export function useFastMarketsInfoRequest(chainId: number) {
  const {
    data: fastMarketInfoData,
    error,
    isLoading,
  } = useSWR<RawMarketsInfoData | undefined>([chainId, "useFastMarketsInfoRequest"], {
    refreshInterval: undefined,
    fetcher: async () => {
      try {
        const client = getSubsquidGraphClient(chainId);
        const rawMarketsInfos = await queryPaginated<SquidMarketInfo>(
          async (limit, offset) =>
            client
              ?.query<{ marketInfos: SquidMarketInfo[] }>({
                query: MARKETS_INFO_QUERY,
                variables: { limit, offset },
                fetchPolicy: "no-cache",
              })
              .then((response) => response?.data?.marketInfos ?? []) ?? [],
          MARKETS_INFO_QUERY_LIMIT
        );

        if (!rawMarketsInfos) {
          return undefined;
        }

        return rawMarketsInfos.reduce((acc: RawMarketsInfoData, mInfo) => {
          const nativeIndexAddress = convertTokenAddress(chainId, mInfo.indexTokenAddress, "native");

          // Skip markets with tokens not in the config to avoid throwing
          if (
            !isValidTokenSafe(chainId, nativeIndexAddress) ||
            !isValidTokenSafe(chainId, mInfo.longTokenAddress) ||
            !isValidTokenSafe(chainId, mInfo.shortTokenAddress)
          ) {
            return acc;
          }

          const indexToken = getToken(chainId, nativeIndexAddress);
          const longToken = getToken(chainId, mInfo.longTokenAddress);
          const shortToken = getToken(chainId, mInfo.shortTokenAddress);
          const isSpotOnly = mInfo.indexTokenAddress === zeroAddress;

          const name = getMarketFullName({ indexToken, longToken, shortToken, isSpotOnly });

          acc[mInfo.marketTokenAddress] = {
            marketTokenAddress: mInfo.marketTokenAddress,
            indexTokenAddress: mInfo.indexTokenAddress,
            longTokenAddress: mInfo.longTokenAddress,
            shortTokenAddress: mInfo.shortTokenAddress,

            isDisabled: mInfo.isDisabled,
            isSameCollaterals: mInfo.longTokenAddress === mInfo.shortTokenAddress,
            isSpotOnly,
            name,
            data: "",

            longPoolAmount: BigInt(mInfo.longPoolAmount),
            shortPoolAmount: BigInt(mInfo.shortPoolAmount),

            maxLongPoolAmount: BigInt(mInfo.maxLongPoolAmount),
            maxShortPoolAmount: BigInt(mInfo.maxShortPoolAmount),
            maxLongPoolUsdForDeposit: BigInt(mInfo.maxLongPoolUsdForDeposit),
            maxShortPoolUsdForDeposit: BigInt(mInfo.maxShortPoolUsdForDeposit),

            poolValueMax: BigInt(mInfo.poolValueMax),
            poolValueMin: BigInt(mInfo.poolValueMin),

            reserveFactorLong: BigInt(mInfo.reserveFactorLong),
            reserveFactorShort: BigInt(mInfo.reserveFactorShort),

            openInterestReserveFactorLong: BigInt(mInfo.openInterestReserveFactorLong),
            openInterestReserveFactorShort: BigInt(mInfo.openInterestReserveFactorShort),

            maxOpenInterestLong: BigInt(mInfo.maxOpenInterestLong),
            maxOpenInterestShort: BigInt(mInfo.maxOpenInterestShort),

            borrowingFactorLong: 0n,
            borrowingFactorShort: 0n,
            borrowingExponentFactorLong: 0n,
            borrowingExponentFactorShort: 0n,

            fundingFactor: BigInt(mInfo.fundingFactor),
            fundingExponentFactor: BigInt(mInfo.fundingExponentFactor),
            fundingIncreaseFactorPerSecond: BigInt(mInfo.fundingIncreaseFactorPerSecond),
            fundingDecreaseFactorPerSecond: BigInt(mInfo.fundingDecreaseFactorPerSecond),
            thresholdForStableFunding: BigInt(mInfo.thresholdForStableFunding),
            thresholdForDecreaseFunding: BigInt(mInfo.thresholdForDecreaseFunding),
            minFundingFactorPerSecond: BigInt(mInfo.minFundingFactorPerSecond),
            maxFundingFactorPerSecond: BigInt(mInfo.maxFundingFactorPerSecond),

            totalBorrowingFees: BigInt(mInfo.totalBorrowingFees),

            positionImpactPoolAmount: BigInt(mInfo.positionImpactPoolAmount),
            minPositionImpactPoolAmount: BigInt(mInfo.minPositionImpactPoolAmount),
            positionImpactPoolDistributionRate: BigInt(mInfo.positionImpactPoolDistributionRate),

            minCollateralFactor: BigInt(mInfo.minCollateralFactor),
            minCollateralFactorForLiquidation: BigInt(mInfo.minCollateralFactorForLiquidation),
            minCollateralFactorForOpenInterestLong: BigInt(mInfo.minCollateralFactorForOpenInterestLong),
            minCollateralFactorForOpenInterestShort: BigInt(mInfo.minCollateralFactorForOpenInterestShort),

            swapImpactPoolAmountLong: BigInt(mInfo.swapImpactPoolAmountLong),
            swapImpactPoolAmountShort: BigInt(mInfo.swapImpactPoolAmountShort),

            atomicSwapFeeFactor: BigInt(mInfo.atomicSwapFeeFactor),

            maxPnlFactorForTradersLong: BigInt(mInfo.maxPnlFactorForTradersLong),
            maxPnlFactorForTradersShort: BigInt(mInfo.maxPnlFactorForTradersShort),

            longInterestUsd: BigInt(mInfo.longOpenInterestUsd),
            shortInterestUsd: BigInt(mInfo.shortOpenInterestUsd),
            longInterestInTokens: BigInt(mInfo.longOpenInterestInTokens),
            shortInterestInTokens: BigInt(mInfo.shortOpenInterestInTokens),

            positionFeeFactorForBalanceWasImproved: BigInt(mInfo.positionFeeFactorForPositiveImpact),
            positionFeeFactorForBalanceWasNotImproved: BigInt(mInfo.positionFeeFactorForNegativeImpact),
            positionImpactFactorPositive: BigInt(mInfo.positionImpactFactorPositive),
            positionImpactFactorNegative: BigInt(mInfo.positionImpactFactorNegative),
            maxPositionImpactFactorPositive: BigInt(mInfo.maxPositionImpactFactorPositive),
            maxPositionImpactFactorNegative: BigInt(mInfo.maxPositionImpactFactorNegative),
            maxPositionImpactFactorForLiquidations: BigInt(mInfo.maxPositionImpactFactorForLiquidations),
            maxLendableImpactFactor: BigInt(mInfo.maxLendableImpactFactor),
            maxLendableImpactFactorForWithdrawals: BigInt(mInfo.maxLendableImpactFactorForWithdrawals),
            maxLendableImpactUsd: BigInt(mInfo.maxLendableImpactUsd),
            lentPositionImpactPoolAmount: BigInt(mInfo.lentPositionImpactPoolAmount),
            positionImpactExponentFactorPositive: BigInt(mInfo.positionImpactExponentFactorPositive),
            positionImpactExponentFactorNegative: BigInt(mInfo.positionImpactExponentFactorNegative),

            swapFeeFactorForBalanceWasImproved: BigInt(mInfo.swapFeeFactorForPositiveImpact),
            swapFeeFactorForBalanceWasNotImproved: BigInt(mInfo.swapFeeFactorForNegativeImpact),
            swapImpactFactorPositive: BigInt(mInfo.swapImpactFactorPositive),
            swapImpactFactorNegative: BigInt(mInfo.swapImpactFactorNegative),
            swapImpactExponentFactor: BigInt(mInfo.swapImpactExponentFactor),

            borrowingFactorPerSecondForLongs: BigInt(mInfo.borrowingFactorPerSecondForLongs),
            borrowingFactorPerSecondForShorts: BigInt(mInfo.borrowingFactorPerSecondForShorts),

            fundingFactorPerSecond: BigInt(mInfo.fundingFactorPerSecond),
            longsPayShorts: mInfo.longsPayShorts,

            virtualPoolAmountForLongToken: BigInt(mInfo.virtualPoolAmountForLongToken),
            virtualPoolAmountForShortToken: BigInt(mInfo.virtualPoolAmountForShortToken),
            virtualInventoryForPositions: BigInt(mInfo.virtualInventoryForPositions),

            useOpenInterestInTokensForBalance: mInfo.useOpenInterestInTokensForBalance,

            virtualMarketId: mInfo.virtualMarketId,
            virtualLongTokenId: mInfo.virtualLongTokenId,
            virtualShortTokenId: mInfo.virtualShortTokenId,
          };

          return acc;
        }, {} as RawMarketsInfoData);
      } catch (e) {
        metrics.pushError(e, "useFastMarketsInfoRequest");
        throw e;
      }
    },
  });

  return useMemo(
    () => ({
      fastMarketInfoData,
      error,
      isLoading,
    }),
    [error, fastMarketInfoData, isLoading]
  );
}
